import { Instance, SnapshotIn, types } from "mobx-state-tree";
import Assessment from "./Assessment";
import { MedicalAssessmentSnapshot } from "./MedicalAssessmentData";
import { v4 } from "uuid";
import { LegalAssessmentSnapshot } from "./LegalAssessmentData";
import { FamilyAssessmentSnapshot } from "./FamilyAssessmentData";
import { SpiritualAssessmentSnapshot } from "./SpiritualAssessmentData";

const _recipient = types
  .model("Recipient", {
    id: types.string,
    birthday: types.string,
    zipCode: types.optional(types.string, ''),
    partnerCode: types.maybeNull(types.string),
    gender: types.string,
    relationship: types.string,
    dateCreated: types.string,
    firstName: types.string,
    lastName: types.string,
    notes: types.maybeNull(types.string),
    imgUrl: types.maybeNull(types.string),
    medicalAssessment: Assessment,
    legalAssessment: Assessment,
    familyAssessment: Assessment,
    spiritualAssessment: Assessment,
  })
  .views((self: { familyAssessment: { scoreComplete: any; }; spiritualAssessment: { scoreComplete: any; }; medicalAssessment: { scoreComplete: any; }; legalAssessment: { scoreComplete: any; }; }) => ({
    get familyScoreComplete() {
      return self.familyAssessment.scoreComplete;
    },
    get spiritualScoreComplete() {
      return self.spiritualAssessment.scoreComplete;
    },
    get medicalScoreComplete() {
      return self.medicalAssessment.scoreComplete;
    },
    get legalScoreComplete() {
      return self.legalAssessment.scoreComplete;
    },
    get overallScore() {
      const score = ((self as any).legalScoreComplete +
        (self as any).familyScoreComplete +
        (self as any).spiritualScoreComplete +
        (self as any).medicalScoreComplete) /
        4;

      return +score.toFixed(0);
    }
  }))
  .actions((self: { firstName: string|null; lastName: string|null; birthday: string|null; gender: string|null; relationship: string|null; zipCode: string|null; notes: string|null; imgUrl: string | null;}) => {
    return {
      setFirstName(name: string) {
        self.firstName = name;
      },
      setLastName(name: string) {
        self.lastName = name;
      },
      setBirthday(birthay: string) {
        self.birthday = birthay;
      },
      setGender(gender: string) {
        self.gender = gender;
      },
      setRelationship(relationship: string) {
        self.relationship = relationship;
      },
      setZipcode(zipCode: string) {
        self.zipCode = zipCode;
      },
      setNotes(notes: string) {
        self.notes = notes;
      },
      setImgUrl (imgUrl: string|null) {
        self.imgUrl = imgUrl;
      }
    };
  });

export const Recipient = types.snapshotProcessor(_recipient, {
  // from snapshot to instance
  preProcessor(snapshot: IRecipientModelSnapshotIn) {
    if (!snapshot) {
      return defaultRecipientSnapshot();
    }

    const recipientSnapshot = {
      ...snapshot,
      medicalAssessment:
        snapshot.medicalAssessment || MedicalAssessmentSnapshot,
      legalAssessment: snapshot.legalAssessment || LegalAssessmentSnapshot,
      familyAssessment: snapshot.familyAssessment || FamilyAssessmentSnapshot,
      spiritualAssessment:
        snapshot.spiritualAssessment || SpiritualAssessmentSnapshot,
    };

    addAssessmentSlideKeys(recipientSnapshot);

    return recipientSnapshot;
  },
});


const addAssessmentSlideKeys = (recipient: IRecipientModelSnapshotIn) => {
  recipient.medicalAssessment.infoSlideKeys = [
    "MedicalDomainTask1",
    "MedicalDomainTask2",
    "MedicalDomainTask3",
    "MedicalDomainTask4",
    "MedicalDomainTask5",
    "MedicalDomainTask6",
    "MedicalDomainTask7",
    "MedicalDomainTask8",
    "MedicalDomainTask9",
  ];

  recipient.legalAssessment.infoSlideKeys = [
    "LegalDomainTask1",
    "LegalDomainTask2",
    "LegalDomainTask3",
    "LegalDomainTask4",
    "LegalDomainTask5",
    "LegalDomainTask6",
    "LegalDomainTask7",
    "LegalDomainTask8",
    "LegalDomainTask9",
  ];

  recipient.familyAssessment.infoSlideKeys = [
    "FamilyDomainTask1",
    "FamilyDomainTask2",
    "FamilyDomainTask3",
    "FamilyDomainTask4",
    "FamilyDomainTask5",
    "FamilyDomainTask6",
    "FamilyDomainTask7",
    "FamilyDomainTask8",
    "FamilyDomainTask9",
    "FamilyDomainTask10",
  ];

  recipient.spiritualAssessment.infoSlideKeys = [
    "SpiritualDomainTask1",
    "SpiritualDomainTask2",
    "SpiritualDomainTask3",
    "SpiritualDomainTask4",
    "SpiritualDomainTask5",
    "SpiritualDomainTask6",
    "SpiritualDomainTask7",
    "SpiritualDomainTask8",
    "SpiritualDomainTask9",
  ];
};

const defaultRecipientSnapshot = () => {
  const defaultSnapshot = {
    id: v4(),
    birthday: "",
    gender: "",
    dateCreated: new Date().toISOString(),
    firstName: "",
    lastName: "",
    relationship: "",
    notes: "",
    overallScore: 0,
    medicalAssessment: MedicalAssessmentSnapshot,
    legalAssessment: LegalAssessmentSnapshot,
    familyAssessment: FamilyAssessmentSnapshot,
    spiritualAssessment: SpiritualAssessmentSnapshot,
    familyScoreComplete: 0,
    spiritualScoreComplete: 0,
    medicalScoreComplete: 0,
    legalScoreComplete: 0,
  };

  return defaultSnapshot;
};

export interface IRecipientModel extends Instance<typeof Recipient> {
}
export interface IRecipientModelSnapshotIn
  extends SnapshotIn<typeof _recipient> {
  spiritualAssessment: any;
  familyAssessment: any;
  legalAssessment: any;
  medicalAssessment: any;
  setNotes: any;
  setZipcode(zipCode: any): unknown;
  setRelationship(relationship: any): unknown;
  setBirthday(birthday: any): unknown;
  setGender(gender: any): unknown;
  setLastName(lastName: any): unknown;
  setFirstName(firstName: any): unknown;
  setImgUrl(imgUrl: any): unknown;
  id: any;
  relationship: any;
  firstName: any;
  lastName: any;
  gender: any;
  birthday: any;
  notes: any;
  zipCode: any;
  imgUrl: any;
}
